/**
 * Toast copy for task-service actions (CT-5093 / CT-4854 contract).
 *
 * Errors thrown by {@link reassignTask} / {@link resolveTask} carry:
 *   - `.code` — msgCode when the GS envelope is present (TASK_NOT_FOUND, …)
 *   - `.status` — internal statusCode, or the bare HTTP status on the defensive path
 *   - `.message` — msgCode description, or the HTTP error text
 *
 * Known contract codes (Tasks API, 2026-09-01):
 *   TASK_NOT_FOUND | TASK_NOT_OPEN | INVALID_UUID | TASK_INTERNAL_ERROR
 *
 * Defensive fallback: bare HTTP 500 with no envelope (observed on unknown uuid in beta).
 */

/** msgCode → locale key prefix under `task.*`. */
export const TASK_ACTION_ERROR_KEYS = {
  TASK_NOT_FOUND: 'errorNotFound',
  TASK_NOT_OPEN: 'errorNotOpen',
  INVALID_UUID: 'errorInvalidUuid',
  TASK_INTERNAL_ERROR: 'errorInternal',
  // Client-side guard: the task row lacked the ids required to call GS (no request made).
  TASK_IDS_MISSING: 'errorIdsMissing',
  // Client-side guard: this event type has no backend to write the owner to at all
  // (only interim `task` rows do), so retrying cannot help — say that, don't ask
  // the user to reload.
  TEAM_ACTION_UNSUPPORTED: 'errorTeamUnsupported',
};

/** action → fallback locale keys when no contract code / bare-500 matched. */
const ACTION_FALLBACK_KEYS = {
  reassign: { title: 'task.reassignFailedTitle', message: 'task.reassignFailedMessage' },
  resolve: { title: 'task.resolveFailedTitle', message: 'task.resolveFailedMessage' },
  escalate: { title: 'escalate.failedTitle', message: 'escalate.failedMessage' },
};

/**
 * @param {import('@aimsos/i18n').TFunction} t
 * @param {Error & { code?: string, status?: number }} [error]
 * @param {'reassign'|'resolve'|'escalate'} action
 * @returns {{ title: string, message: string }}
 */
export function taskActionErrorToast(t, error, action = 'resolve') {
  const code = error?.code;
  const keyPrefix = code && TASK_ACTION_ERROR_KEYS[code];
  if (keyPrefix) {
    return {
      title: t(`task.${keyPrefix}Title`),
      message: t(`task.${keyPrefix}Message`),
    };
  }

  // Bare HTTP error — no GS envelope (e.g. HTTP 500 "Internal Server Error").
  if (error?.status === 500 && !code) {
    return {
      title: t('task.errorHttp500Title'),
      message: t('task.errorHttp500Message'),
    };
  }

  const fallback = ACTION_FALLBACK_KEYS[action] ?? ACTION_FALLBACK_KEYS.resolve;
  return {
    title: t(fallback.title),
    message: error?.message || t(fallback.message),
  };
}
